import React, { useRef, useState, useEffect, useCallback } from 'react';

import { ChatBubblePosition, saveChatBubblePosition } from 'utils/storage';

interface UseDraggableChatBubbleProps {
  buttonSize: number; // Size of the button in pixels
  userId: number | string | undefined;
  initialPosition?: ChatBubblePosition;
}

interface Position {
  x: number;
  y: number;
}

export const useDraggableChatBubble = ({
  userId,
  buttonSize,
  initialPosition,
}: UseDraggableChatBubbleProps) => {
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [edge, setEdge] = useState<'left' | 'right'>('right');
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const dragStartPos = useRef<Position>({ x: 0, y: 0 });
  const dragStartMousePos = useRef<Position>({ x: 0, y: 0 });
  const elementRef = useRef<HTMLButtonElement | null>(null);

  const EDGE_ZONE_WIDTH = 150;

  useEffect(() => {
    if (!userId) {
      setEdge('right');
      setPosition({
        x: window.innerWidth - 28 - buttonSize,
        y: window.innerHeight - 28 - buttonSize,
      });
      return;
    }

    const savedPosition = initialPosition;
    if (savedPosition) {
      setEdge(savedPosition.edge);
      // Calculate x position based on edge
      const x =
        savedPosition.edge === 'left'
          ? EDGE_ZONE_WIDTH / 2 - buttonSize / 2
          : window.innerWidth - EDGE_ZONE_WIDTH / 2 - buttonSize / 2;
      setPosition({ x, y: savedPosition.top });
    } else {
      // Default position: bottom right
      setEdge('right');
      setPosition({
        x: window.innerWidth - 28 - buttonSize,
        y: window.innerHeight - 28 - buttonSize,
      });
    }
  }, [buttonSize, initialPosition, userId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (!elementRef.current) {
        return;
      }

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const currentX = position.x;
      const currentY = position.y;

      // Ensure bubble stays within bounds
      let newX = currentX;
      let newY = currentY;

      // Check if bubble is on left or right edge
      const isOnLeft = currentX < windowWidth / 2;
      const newEdge = isOnLeft ? 'left' : 'right';

      // Snap to edge zone
      if (newEdge === 'left') {
        newX = Math.max(0, Math.min(EDGE_ZONE_WIDTH - buttonSize, currentX));
      } else {
        newX = Math.max(
          windowWidth - EDGE_ZONE_WIDTH,
          Math.min(windowWidth - buttonSize, currentX)
        );
      }

      // Ensure vertical position is within bounds
      newY = Math.max(0, Math.min(windowHeight - buttonSize, currentY));

      setPosition({ x: newX, y: newY });
      setEdge(newEdge);

      // Save position
      if (userId) {
        saveChatBubblePosition(userId, {
          top: newY,
          edge: newEdge,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position.x, position.y, userId, buttonSize]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Prevent default to avoid text selection
      e.preventDefault();
      setIsDragging(true);
      setHasMoved(false); // Reset movement flag
      dragStartPos.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
      dragStartMousePos.current = {
        x: e.clientX,
        y: e.clientY,
      };
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) {
        return;
      }

      // Check if mouse has moved significantly (more than 5px)
      const deltaX = Math.abs(e.clientX - dragStartMousePos.current.x);
      const deltaY = Math.abs(e.clientY - dragStartMousePos.current.y);
      if (deltaX > 5 || deltaY > 5) {
        setHasMoved(true);
      }

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let newX = e.clientX - dragStartPos.current.x;
      let newY = e.clientY - dragStartPos.current.y;

      // Constrain Y position
      newY = Math.max(0, Math.min(windowHeight - buttonSize, newY));

      // Determine which edge zone the mouse is in
      const mouseX = e.clientX;
      const isInLeftZone = mouseX < EDGE_ZONE_WIDTH;
      const isInRightZone = mouseX > windowWidth - EDGE_ZONE_WIDTH;

      // If in center, snap to nearest edge
      if (!isInLeftZone && !isInRightZone) {
        const distanceToLeft = mouseX;
        const distanceToRight = windowWidth - mouseX;
        if (distanceToLeft < distanceToRight) {
          // Snap to left
          newX = Math.max(0, Math.min(EDGE_ZONE_WIDTH - buttonSize, newX));
        } else {
          // Snap to right
          newX = Math.max(
            windowWidth - EDGE_ZONE_WIDTH,
            Math.min(windowWidth - buttonSize, newX)
          );
        }
      } else if (isInLeftZone) {
        // Constrain to left edge zone
        newX = Math.max(0, Math.min(EDGE_ZONE_WIDTH - buttonSize, newX));
      } else if (isInRightZone) {
        // Constrain to right edge zone
        newX = Math.max(
          windowWidth - EDGE_ZONE_WIDTH,
          Math.min(windowWidth - buttonSize, newX)
        );
      }

      setPosition({ x: newX, y: newY });
    },
    [isDragging, buttonSize]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) {
      return;
    }

    setIsDragging(false);

    // Reset hasMoved after a short delay to allow click handler to check it
    setTimeout(() => {
      setHasMoved(false);
    }, 100);

    // Get current position state
    const currentPos = position;
    const windowWidth = window.innerWidth;
    const finalX = currentPos.x;
    const isOnLeft = finalX < windowWidth / 2;
    const finalEdge = isOnLeft ? 'left' : 'right';

    // Snap to nearest edge zone center
    let snappedX = finalX;
    if (finalEdge === 'left') {
      snappedX = Math.max(0, EDGE_ZONE_WIDTH / 2 - buttonSize / 2);
    } else {
      snappedX = Math.min(
        windowWidth - buttonSize,
        windowWidth - EDGE_ZONE_WIDTH / 2 - buttonSize / 2
      );
    }

    const finalY = Math.max(
      0,
      Math.min(window.innerHeight - buttonSize, currentPos.y)
    );

    setPosition({ y: finalY, x: snappedX });
    setEdge(finalEdge);

    // Save position
    if (userId) {
      saveChatBubblePosition(userId, {
        top: finalY,
        edge: finalEdge,
      });
    }
  }, [buttonSize, isDragging, position, userId]);

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    edge,
    hasMoved,
    position,
    isDragging,
    handleMouseDown,
    elementRef: elementRef as React.RefObject<HTMLButtonElement>,
  };
};
